export const researchPillars = [
  {
    id: "reflection",
    title: "How can AI support people in making sense of difficult or personally meaningful experiences without prescribing what those experiences mean?",
    shortLabel: "Designing AI Systems to Responsibly Scaffolding Care Conversations",
    description: "Many care conversations about mental health, emotional support, or end-of-life planning are complex, deeply personal, and value-laden. While AI is increasingly introduced to assist, it can easily oversimplify or intrude on these reflective processes. The challenge is to design systems that support dialogue and reflection without replacing human judgement.",
    bigGoal: "Reflection",
    associations: {
      publications: [
        "\"I'm Not Able to Be There for You\": Emotional Labour, Responsibility, and AI in Peer Support",
        "Redistributing Voice and Responsibility: AI in Relationship-Centred Care",
        "Words to Describe What I'm Feeling: Exploring the Potential of AI Agents for High Subjectivity Decisions in Advance Care Planning",
        "\"I Said Things I Needed to Hear Myself\": Peer Support as an Emotional, Organisational, and Sociotechnical Practice in Singapore",
        "Envisioning an AI-Enhanced Mental Health Ecosystem",
        "Towards Understanding Emotions for Engaged Mental Health Conversations",
        "Designing AI-Based Conversational Agent for Diabetes Care in a Multilingual Context",
        "Social Behaviour Understanding Using Deep Neural Networks: Development of Social Intelligence Systems"
      ],
      news: [
        "🏅 Named 1 of 10 DBCS Top Seed Award Winners for 2026!",
        "🇸🇬 Attended SG HCI Meetup 2026 in Singapore! Gave an oral presentation on my peer support research (DIS26 PWiP and CSCW26 full paper).",
        "🇸🇬 Attended DIS 2026 in Singapore! Presented 2 PWiP papers and served as a Student Volunteer.",
        "🇪🇸 Attended CHI 2026 in Barcelona, Spain! Presented a full paper and a workshop paper, took part in a workshop and attended a meet-up.",
        "📣 First-author full paper accepted at CSCW 2026.",
        "📣 Two first-author PWiP papers accepted at DIS 2026.",
        "🏅 Our CHI 2026 full paper received an Honourable Mention Award.",
        "📣 First-author workshop paper accepted at CHI 2026.",
        "📣 First-author full paper accepted at CHI 2026.",
        "🇯🇵 Attended CHI 2025 in Yokohama, Japan! Presented a workshop paper.",
        "📣 First-author workshop paper accepted at CHI 2025.",
        "🇩🇰 Attended DIS 2024 in Copenhagen, Denmark! Presented a poster.",
        "📣 First-author PWiP paper accepted at DIS 2024."
      ],
      projects: [
        "waves",
        "trust",
        "acpchat",
        "empathai"
      ],
      features: [
        "ACPChat research (CHI26 paper) featured on <a href=\"https://scienmag.com/new-tech-enables-communication-for-nonverbal-patients/\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-green dark:text-green-dark underline underline-offset-2\">ScienMag</a>, <a href=\"https://bioengineer.org/new-tech-enables-communication-for-nonverbal-patients/\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-green dark:text-green-dark underline underline-offset-2\">Bioengineer.org</a>, and <a href=\"https://techxplore.com/news/2026-07-ai-agent-machines-patients-life.html\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-green dark:text-green-dark underline underline-offset-2\">TechXplore</a>."
      ]
    },
    projectIds: [],
    flagshipProjectIds: [
      "acpchat",
      "empathai"
    ]
  },
  {
    id: "misalignment",
    title: "What happens when AI systems encounter care practices, values, and communities whose norms do not align with their assumptions?",
    shortLabel: "Aligning AI with Norms and Needs of Care Communities",
    description: "AI systems trained on generic empathy patterns might reproduce scripts that do not fit diverse social and cultural realities. Such misalignment can lead to misunderstandings of distress, pressure to conform to normative responses, or erosion of trust. Understanding these gaps is essential for designing systems that respect the practices and values of care communities.",
    bigGoal: "Alignment",
    associations: {
      projects: [
        "on-device-slm-mental-health",
        "llms-pfa",
        "meralion-crisis-care",
        "trust",
        "sense",
        "forge",
        "heartalk",
        "empathai",
        "blossom"
      ],
      publications: [
        "\"Is This Really a Human Peer Supporter?\": Misalignments Between Peer Supporters and Experts in LLM-Supported Interactions"
      ],
      news: [
        "🇺🇸 Attending CSCW 2026 in Salt Lake City, Utah, USA! Presenting 2 full papers and serving as a Student Volunteer.",
        "📣 First-author full paper accepted at CSCW 2026."
      ]
    }
  },
  {
    id: "agency",
    title: "How can we design human-AI interactions that preserve people's ability to steer, question, reject, and act on AI contributions?",
    shortLabel: "Human-AI Collaboration to Support Reflection and Decision-Making without Harm or Risk",
    description: "AI tools can easily overtake user agency by producing fluent or “perfect” responses. In sensitive care contexts, authenticity, uncertainty, and self-expression are often more important than efficiency. The design challenge is to enable collaborative interactions that support reflection while preserving human agency.",
    bigGoal: "Agency",
    associations: {
      projects: [
        "waves",
        "trust",
        "acpchat",
        "truthguardian"
      ],
      news: [
        "🇺🇸 Attending CSCW 2026 in Salt Lake City, Utah, USA! Presenting 2 full papers and serving as a Student Volunteer.",
        "🇸🇬 Attended DIS 2026 in Singapore! Presented 2 PWiP papers and served as a Student Volunteer.",
        "🇪🇸 Attended CHI 2026 in Barcelona, Spain! Presented a full paper and a workshop paper, took part in a workshop and attended a meet-up.",
        "📣 Two first-author PWiP papers accepted at DIS 2026.",
        "📣 Third-author full paper accepted at CSCW 2026.",
        "🇳🇴 Attended CSCW 2025 in Bergen, Norway! Presented a poster.",
        "📣 Third-author poster paper accepted at CSCW 2025."
      ],
      publications: [
        "Sword and Shield: Uses and Strategies of LLMs in Navigating Disinformation",
        "Acts of Configuration: Rethinking Provenance, Temporality and Legitimacy in Post-Mortem Agents",
        "Communication Games to Study Emerging Disinformation Scenarios With LLMs"
      ]
    }
  }
];

export const researchAgendaIntro = {
  title: "Designing Responsible Human-AI Interaction in Sensitive Care Contexts",
  principles: [
    "Preserve human agency and authorship",
    "Scaffold reflection and deliberation",
    "Acknowledge boundaries and adapt to contexts",
    "Minimise harm in emotionally vulnerable situations"
  ],
  focus: "I focus on how people engage with AI in conversation and decision-making in domains where emotional vulnerability is present, values and preferences are uncertain, and decisions affect family, caregivers, and support relationships, reshaping trust, responsibility, and authority between people."
};
